import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from './ui/table';
import { 
  Plus, 
  Trash2, 
  Calculator, 
  Download,
  AlertTriangle,
  CheckCircle,
  Info
} from 'lucide-react';
import { toast } from 'sonner';

// Voci retributive standard
const VOCI_STANDARD = [
  { codice: 'PAGA_BASE', descrizione: 'Paga Base / Minimo Tabellare', tipo: 'competenza' },
  { codice: 'CONTINGENZA', descrizione: 'Contingenza', tipo: 'competenza' },
  { codice: 'TERZO_ELEM', descrizione: 'Terzo Elemento', tipo: 'competenza' },
  { codice: 'EDR', descrizione: 'E.D.R.', tipo: 'competenza' },
  { codice: 'SUPERMINIMO', descrizione: 'Superminimo', tipo: 'competenza' },
  { codice: 'SCATTI_ANZ', descrizione: 'Scatti Anzianità', tipo: 'competenza' },
  { codice: 'STRAORD_FER', descrizione: 'Straordinario Feriale', tipo: 'competenza' },
  { codice: 'STRAORD_NOT', descrizione: 'Straordinario Notturno', tipo: 'competenza' },
  { codice: 'STRAORD_FES', descrizione: 'Straordinario Festivo', tipo: 'competenza' },
  { codice: 'NOTTURNO', descrizione: 'Maggiorazione Notturna', tipo: 'competenza' },
  { codice: 'FESTIVITA', descrizione: 'Festività', tipo: 'competenza' },
  { codice: 'FERIE', descrizione: 'Ferie Godute', tipo: 'competenza' },
  { codice: 'ROL', descrizione: 'Permessi ROL', tipo: 'competenza' },
  { codice: 'MALATTIA', descrizione: 'Malattia', tipo: 'competenza' },
  { codice: 'INFORTUNIO', descrizione: 'Infortunio', tipo: 'competenza' },
  { codice: 'MATERNITA', descrizione: 'Maternità', tipo: 'competenza' },
  { codice: 'PERMESSO_104', descrizione: 'Permesso L.104', tipo: 'competenza' },
  { codice: 'RATEO_13', descrizione: 'Rateo 13ª Mensilità', tipo: 'competenza' },
  { codice: 'RATEO_14', descrizione: 'Rateo 14ª Mensilità', tipo: 'competenza' },
  { codice: 'INDENNITA_TURNO', descrizione: 'Indennità Turno', tipo: 'competenza' },
  { codice: 'INDENNITA_TRASP', descrizione: 'Indennità Trasporto', tipo: 'competenza' },
  { codice: 'INDENNITA_MENSA', descrizione: 'Indennità Mensa', tipo: 'competenza' },
  { codice: 'PREMI_BONUS', descrizione: 'Premi e Bonus', tipo: 'competenza' },
  { codice: 'INPS', descrizione: 'Contributi INPS', tipo: 'trattenuta' },
  { codice: 'IRPEF', descrizione: 'IRPEF', tipo: 'trattenuta' },
  { codice: 'ADD_REG', descrizione: 'Addizionale Regionale', tipo: 'trattenuta' },
  { codice: 'ADD_COM', descrizione: 'Addizionale Comunale', tipo: 'trattenuta' },
  { codice: 'ALTRE_TRATT', descrizione: 'Altre Trattenute', tipo: 'trattenuta' }
];

export default function PayslipComparisonTable({ 
  extractedData = {}, 
  ccnlData = {},
  onCalculate 
}) {
  const [voci, setVoci] = useState(() => {
    // Inizializza con i dati estratti
    return VOCI_STANDARD.map(voce => {
      let quotaRiconosciuta = 0;
      let quotaDovuta = 0;
      let quantita = 0;
      
      // Mappa i dati estratti alle voci
      switch(voce.codice) {
        case 'PAGA_BASE':
          quotaRiconosciuta = extractedData.paga_base || 0;
          quotaDovuta = ccnlData.paga_base || quotaRiconosciuta;
          break;
        case 'CONTINGENZA':
          quotaRiconosciuta = extractedData.contingenza || 0;
          quotaDovuta = ccnlData.contingenza || quotaRiconosciuta;
          break;
        case 'SCATTI_ANZ':
          quotaRiconosciuta = extractedData.scatti_anzianita_importo || extractedData.importo_scatti || 0;
          quantita = extractedData.scatti_anzianita_numero || 0;
          quotaDovuta = ccnlData.importo_scatti_previsto || quotaRiconosciuta;
          break;
        case 'STRAORD_FER':
          quotaRiconosciuta = extractedData.importo_straordinario_feriale || extractedData.importo_straordinario || 0;
          quantita = extractedData.ore_straordinario_feriale || extractedData.ore_straordinario || 0;
          break;
        case 'STRAORD_NOT':
          quotaRiconosciuta = extractedData.importo_straordinario_notturno || 0;
          quantita = extractedData.ore_straordinario_notturno || 0;
          break;
        case 'STRAORD_FES':
          quotaRiconosciuta = extractedData.importo_straordinario_festivo || 0;
          quantita = extractedData.ore_straordinario_festivo || 0;
          break;
        case 'NOTTURNO':
          quotaRiconosciuta = extractedData.importo_lavoro_notturno || 0;
          quantita = extractedData.ore_notturne || 0;
          break;
        case 'MALATTIA':
          quotaRiconosciuta = extractedData.importo_malattia || 0;
          quantita = extractedData.giorni_malattia || 0;
          break;
        case 'INFORTUNIO':
          quotaRiconosciuta = extractedData.importo_infortunio || 0;
          quantita = extractedData.giorni_infortunio || 0;
          break;
        case 'MATERNITA':
          quotaRiconosciuta = extractedData.importo_maternita || 0;
          quantita = extractedData.giorni_maternita || 0;
          break;
        case 'PERMESSO_104':
          quantita = extractedData.giorni_permesso_104 || 0;
          break;
        case 'FERIE':
          quotaRiconosciuta = extractedData.importo_ferie || 0;
          quantita = extractedData.giorni_ferie_godute || 0;
          break;
        case 'RATEO_13':
          quotaRiconosciuta = extractedData.importo_13esima || 0;
          break;
        case 'RATEO_14':
          quotaRiconosciuta = extractedData.importo_14esima || 0;
          break;
        case 'INPS':
          quotaRiconosciuta = extractedData.inps_dipendente || extractedData.inps || 0;
          break;
        case 'IRPEF':
          quotaRiconosciuta = extractedData.irpef_netta || extractedData.irpef || 0;
          break;
        case 'ADD_REG':
          quotaRiconosciuta = extractedData.addizionale_regionale || 0;
          break;
        case 'ADD_COM':
          quotaRiconosciuta = extractedData.addizionale_comunale || 0;
          break;
        default:
          break;
      }
      
      return {
        ...voce,
        quantita,
        quotaRiconosciuta,
        quotaDovuta,
        differenza: quotaDovuta - quotaRiconosciuta
      };
    }).filter(v => v.quotaRiconosciuta > 0 || v.quotaDovuta > 0 || v.quantita > 0);
  });
  
  const [showAllVoci, setShowAllVoci] = useState(false);
  
  const updateVoce = (index, field, value) => {
    const newVoci = [...voci];
    newVoci[index][field] = parseFloat(value) || 0;
    newVoci[index].differenza = newVoci[index].quotaDovuta - newVoci[index].quotaRiconosciuta;
    setVoci(newVoci);
  };
  
  const addVoce = (codice) => {
    const voceStandard = VOCI_STANDARD.find(v => v.codice === codice);
    if (voceStandard && !voci.find(v => v.codice === codice)) {
      setVoci([...voci, {
        ...voceStandard,
        quantita: 0,
        quotaRiconosciuta: 0,
        quotaDovuta: 0,
        differenza: 0
      }]);
    }
  };
  
  const removeVoce = (index) => {
    setVoci(voci.filter((_, i) => i !== index));
  };
  
  const totaleCompetenze = voci
    .filter(v => v.tipo === 'competenza')
    .reduce((sum, v) => sum + v.quotaRiconosciuta, 0);
  
  const totaleTrattenute = voci
    .filter(v => v.tipo === 'trattenuta')
    .reduce((sum, v) => sum + v.quotaRiconosciuta, 0);
  
  const totaleCompetenzeDoute = voci
    .filter(v => v.tipo === 'competenza')
    .reduce((sum, v) => sum + v.quotaDovuta, 0);
  
  const totaleDifferenza = voci
    .filter(v => v.tipo === 'competenza')
    .reduce((sum, v) => sum + v.differenza, 0);
  
  const vociNonAggiunte = VOCI_STANDARD.filter(vs => !voci.find(v => v.codice === vs.codice));
  
  const getDifferenzaClass = (diff) => {
    if (diff > 0) return 'text-green-600 font-semibold';
    if (diff < 0) return 'text-red-600 font-semibold';
    return 'text-gray-500';
  };
  
  const exportToCSV = () => {
    const headers = ['Voce', 'Quantità', 'Quota Riconosciuta', 'Quota Dovuta', 'Differenza'];
    const rows = voci.map(v => [
      v.descrizione,
      v.quantita,
      v.quotaRiconosciuta.toFixed(2),
      v.quotaDovuta.toFixed(2),
      v.differenza.toFixed(2)
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(';')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'confronto_busta_paga.csv';
    link.click();
    toast.success('File CSV esportato');
  };

  return (
    <Card className="swiss-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg flex items-center gap-2">
          <Calculator className="w-5 h-5 text-blue-600" />
          Tabella Confronto Dettagliata
        </CardTitle>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={exportToCSV}
          >
            <Download className="w-4 h-4 mr-1" />
            Esporta CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Info Banner */}
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <strong>Istruzioni:</strong> Modifica i valori nelle colonne "Quota Riconosciuta" e "Quota Dovuta" per calcolare le differenze. 
            La quota dovuta viene calcolata automaticamente in base al CCNL selezionato.
          </div>
        </div>
        
        {/* Tabella Competenze */}
        <div className="mb-6">
          <h3 className="font-semibold text-green-700 mb-2 flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            COMPETENZE
          </h3>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-[250px]">Voce</TableHead>
                  <TableHead className="text-right w-[100px]">Quantità</TableHead>
                  <TableHead className="text-right w-[150px]">Quota Riconosciuta €</TableHead>
                  <TableHead className="text-right w-[150px]">Quota Dovuta €</TableHead>
                  <TableHead className="text-right w-[120px]">Differenza €</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {voci.filter(v => v.tipo === 'competenza').map((voce, index) => {
                  const realIndex = voci.findIndex(v => v.codice === voce.codice);
                  return (
                    <TableRow key={voce.codice} className="hover:bg-gray-50">
                      <TableCell className="font-medium">{voce.descrizione}</TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          value={voce.quantita || ''}
                          onChange={(e) => updateVoce(realIndex, 'quantita', e.target.value)}
                          className="w-20 text-right h-8"
                          placeholder="0"
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          step="0.01"
                          value={voce.quotaRiconosciuta || ''}
                          onChange={(e) => updateVoce(realIndex, 'quotaRiconosciuta', e.target.value)}
                          className="w-28 text-right h-8"
                          placeholder="0.00"
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          step="0.01"
                          value={voce.quotaDovuta || ''}
                          onChange={(e) => updateVoce(realIndex, 'quotaDovuta', e.target.value)}
                          className="w-28 text-right h-8"
                          placeholder="0.00"
                        />
                      </TableCell>
                      <TableCell className={`text-right ${getDifferenzaClass(voce.differenza)}`}>
                        {voce.differenza > 0 ? '+' : ''}{voce.differenza.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => removeVoce(realIndex)}
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {/* Totale Competenze */}
                <TableRow className="bg-green-50 font-semibold">
                  <TableCell colSpan={2}>TOTALE COMPETENZE</TableCell>
                  <TableCell className="text-right">{totaleCompetenze.toFixed(2)}</TableCell>
                  <TableCell className="text-right">{totaleCompetenzeDoute.toFixed(2)}</TableCell>
                  <TableCell className={`text-right ${getDifferenzaClass(totaleDifferenza)}`}>
                    {totaleDifferenza > 0 ? '+' : ''}{totaleDifferenza.toFixed(2)}
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>
        
        {/* Tabella Trattenute */}
        <div className="mb-6">
          <h3 className="font-semibold text-red-700 mb-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            TRATTENUTE
          </h3>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-[250px]">Voce</TableHead>
                  <TableHead className="text-right w-[100px]">Quantità</TableHead>
                  <TableHead className="text-right w-[150px]">Importo €</TableHead>
                  <TableHead className="w-[150px]"></TableHead>
                  <TableHead className="w-[120px]"></TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {voci.filter(v => v.tipo === 'trattenuta').map((voce, index) => {
                  const realIndex = voci.findIndex(v => v.codice === voce.codice);
                  return (
                    <TableRow key={voce.codice} className="hover:bg-gray-50">
                      <TableCell className="font-medium">{voce.descrizione}</TableCell>
                      <TableCell></TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          step="0.01"
                          value={voce.quotaRiconosciuta || ''}
                          onChange={(e) => updateVoce(realIndex, 'quotaRiconosciuta', e.target.value)}
                          className="w-28 text-right h-8"
                          placeholder="0.00"
                        />
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => removeVoce(realIndex)}
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {/* Totale Trattenute */}
                <TableRow className="bg-red-50 font-semibold">
                  <TableCell colSpan={2}>TOTALE TRATTENUTE</TableCell>
                  <TableCell className="text-right">{totaleTrattenute.toFixed(2)}</TableCell>
                  <TableCell colSpan={3}></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>
        
        {/* Riepilogo Netto */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-sm text-gray-600">Totale Competenze</div>
              <div className="text-xl font-bold text-green-700">{totaleCompetenze.toFixed(2)} €</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Totale Trattenute</div>
              <div className="text-xl font-bold text-red-700">{totaleTrattenute.toFixed(2)} €</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Netto Stimato</div>
              <div className="text-xl font-bold text-blue-700">{(totaleCompetenze - totaleTrattenute).toFixed(2)} €</div>
            </div>
          </div>
          
          {totaleDifferenza !== 0 && (
            <div className={`mt-4 p-3 rounded-lg ${totaleDifferenza > 0 ? 'bg-green-100 border-green-300' : 'bg-red-100 border-red-300'} border`}>
              <div className="text-center">
                <span className="text-sm font-medium">
                  {totaleDifferenza > 0 ? '✅ Ti spettano' : '⚠️ Differenza negativa di'}
                </span>
                <span className={`text-lg font-bold ml-2 ${totaleDifferenza > 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {Math.abs(totaleDifferenza).toFixed(2)} € / mese
                </span>
                <span className="text-sm text-gray-600 ml-2">
                  ({(Math.abs(totaleDifferenza) * 13).toFixed(2)} € / anno)
                </span>
              </div>
            </div>
          )}
        </div>
        
        {/* Aggiungi Voce */}
        {vociNonAggiunte.length > 0 && (
          <div className="mt-4 flex items-center gap-2">
            <Select onValueChange={addVoce}>
              <SelectTrigger className="w-[300px]">
                <SelectValue placeholder="Aggiungi voce..." />
              </SelectTrigger>
              <SelectContent>
                {vociNonAggiunte.map(voce => (
                  <SelectItem key={voce.codice} value={voce.codice}>
                    {voce.descrizione}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" disabled>
              <Plus className="w-4 h-4 mr-1" />
              Aggiungi
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
